/**
 * TimeFilter V2 - Visual timeline with auto-detected event buckets
 *
 * Features:
 * - Visual timeline bar showing photo density with glowing segments
 * - Auto-clusters photos into event windows (30min gap = new event)
 * - Day/event pills for quick filtering
 * - Futuristic Matrix-style design
 */

import { useCallback, useMemo } from "react";
import { useTimeEvents } from "../../hooks/useTimeEvents";

/**
 * Timeline bar showing glowing segments where photos exist
 */
function TimelineBar({ events, totalSpan, selectedEventIds, onSegmentClick }) {
  // Calculate day boundaries for markers (must be before early return for hooks rules)
  const dayMarkers = useMemo(() => {
    if (!totalSpan) return [];
    const markers = [];
    const startDate = new Date(totalSpan.start);
    const endDate = new Date(totalSpan.end);
    const totalRange = totalSpan.end - totalSpan.start || 1;

    // Start from beginning of start day
    const current = new Date(startDate);
    current.setHours(0, 0, 0, 0);

    // Cap iterations to prevent UI freeze on malformed data (defense-in-depth)
    const MAX_DAY_MARKERS = 365;
    let iterations = 0;
    // Loop terminates: current is incremented by setDate(), iterations capped at 365
    while (current <= endDate && iterations < MAX_DAY_MARKERS) { // NOSONAR S2189
      const ts = current.getTime();
      if (ts >= totalSpan.start && ts <= totalSpan.end) {
        markers.push({
          position: (ts - totalSpan.start) / totalRange,
          label: current.toLocaleDateString([], { month: "short", day: "numeric" }),
        });
      }
      current.setDate(current.getDate() + 1);
      iterations++;
    }
    return markers;
  }, [totalSpan]);

  if (!totalSpan || events.length === 0) return null;

  return (
    <div className="timeline-bar">
      <div className="timeline-track">
        {/* Day markers */}
        {dayMarkers.map((marker) => (
          <div
            key={`${marker.label}-${marker.position}`}
            className="timeline-day-marker"
            style={{ left: `${marker.position * 100}%` }}
          >
            <span className="timeline-day-label">{marker.label}</span>
          </div>
        ))}

        {/* Event segments */}
        {events.map((event) => {
          const isSelected = selectedEventIds.includes(event.id);
          const width = Math.max(2, (event.positionEnd - event.positionStart) * 100);

          return (
            <div
              key={event.id}
              className={`timeline-segment ${isSelected ? "selected" : ""}`}
              data-density={event.density}
              style={{
                left: `${event.positionStart * 100}%`,
                width: `${width}%`,
              }}
              onClick={() => onSegmentClick(event)}
              title={`${event.period}: ${event.timeRange} (${event.count} photos)`}
            >
              <div className="timeline-segment-fill" />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Individual event pill button
 */
function EventPill({ event, isSelected, onClick }) {
  return (
    <button
      className={`event-pill ${isSelected ? "selected" : ""}`}
      onClick={() => onClick(event)}
      type="button"
    >
      <span className="event-pill-period">{event.period}</span>
      <span className="event-pill-time">{event.timeRange}</span>
      <span className="event-pill-count">{event.count}</span>
    </button>
  );
}

/**
 * Event buckets grouped by day
 */
function EventBuckets({ days, selectedEventIds, onEventClick }) {
  if (days.length === 0) return null;

  return (
    <div className="event-buckets">
      {days.map((day) => (
        <div key={day.date} className="event-day-group">
          <div className="event-day-header">{day.label}</div>
          <div className="event-pills">
            {day.events.map((event) => (
              <EventPill
                key={event.id}
                event={event}
                isSelected={selectedEventIds.includes(event.id)}
                onClick={onEventClick}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Main TimeFilter component
 */
function TimeFilter({
  photos,
  timeRange,
  onChange,
  onClear,
  showTimeline = true,
  rainEnabled = false,
}) {
  // Compute events from photos
  const timeEvents = useTimeEvents(photos);

  // Extract selected event IDs from timeRange
  const selectedEventIds = useMemo(() => {
    return timeRange?.eventIds || [];
  }, [timeRange]);

  // Check if "no timestamp" filter is active
  const noTimestampSelected = timeRange?.noTimestamp === true;

  // Handle clicking the "no timestamp" indicator
  const handleNoTimestampClick = useCallback(() => {
    if (noTimestampSelected) {
      onClear();
    } else {
      onChange({ noTimestamp: true });
    }
  }, [noTimestampSelected, onChange, onClear]);

  // Handle event selection
  const handleEventClick = useCallback(
    (event) => {
      const currentIds = new Set(selectedEventIds);

      if (currentIds.has(event.id)) {
        // Deselect this event
        currentIds.delete(event.id);
      } else {
        // Select this event (single select for simplicity)
        currentIds.clear();
        currentIds.add(event.id);
      }

      if (currentIds.size === 0) {
        // No selection - clear filter
        onClear();
        return;
      }

      // Calculate new time range from selected events
      const selectedEvents = timeEvents.events.filter((e) => currentIds.has(e.id));
      const start = Math.min(...selectedEvents.map((e) => e.start));
      const end = Math.max(...selectedEvents.map((e) => e.end));

      onChange({
        start,
        end,
        eventIds: Array.from(currentIds),
      });
    },
    [selectedEventIds, timeEvents.events, onChange, onClear]
  );

  // Don't render if no photos with timestamps
  if (timeEvents.days.length === 0) {
    if (timeEvents.photosWithoutTime > 0) {
      return (
        <div className={`time-filter-v2 ${rainEnabled ? "matrix-mode" : ""}`}>
          <button
            className={`no-time-indicator clickable ${noTimestampSelected ? "selected" : ""}`}
            onClick={handleNoTimestampClick}
            type="button"
          >
            <span className="no-time-icon">[--:--]</span>
            <span>All {timeEvents.photosWithoutTime} photos lack capture timestamps</span>
          </button>
        </div>
      );
    }
    return null;
  }

  return (
    <div className={`time-filter-v2 ${rainEnabled ? "matrix-mode" : ""}`}>
      {/* Visual timeline bar */}
      {showTimeline && timeEvents.events.length > 1 && (
        <TimelineBar
          events={timeEvents.events}
          totalSpan={timeEvents.totalSpan}
          selectedEventIds={selectedEventIds}
          onSegmentClick={handleEventClick}
        />
      )}

      {/* Event bucket pills */}
      <EventBuckets
        days={timeEvents.days}
        selectedEventIds={selectedEventIds}
        onEventClick={handleEventClick}
      />

      {/* Photos without timestamp indicator - clickable to filter */}
      {timeEvents.photosWithoutTime > 0 && (
        <button
          className={`no-time-indicator clickable ${noTimestampSelected ? "selected" : ""}`}
          onClick={handleNoTimestampClick}
          type="button"
        >
          <span className="no-time-icon">[!]</span>
          <span>+{timeEvents.photosWithoutTime} photos without timestamp</span>
        </button>
      )}
    </div>
  );
}

export default TimeFilter;
